import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Row, Col, FormGroup, Label, FormText, Button } from "reactstrap";
import SimpleReactValidator from "simple-react-validator";
import logoLight from "../../../assets/images/logo-light.webp";
import { __t } from "../../../i18n/translator";
import VendorAuthChips from "./VendorAuthChips";
import {
    vendorConfirmResetTokenRequest,
    vendorResetPasswordRequest,
} from "../../../redux/vendor/auth/forgot/action";
import {
    LockSvg,
    EyeOutlineSvg,
    EyeOffOutlineSvg,
    VendorWaveTopSvg,
    VendorWaveBottomSvg,
    VendorDonutSvg,
} from "../../../components/CommonSvg";

const VendorResetPassword = (props) => {
    const { confirmResetToken, resetVendorPassword, resetPasswordData } = props;
    
    const { token, email } = useParams();
    const navigate = useNavigate();
    const validator = useRef(new SimpleReactValidator());
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const initialValues = {
        password: "",
        confirmPassword: "",
    };

    
    useEffect(() => {
        if (token) {
            confirmResetToken(token);
        }
    }, [token]);

    const handleSubmit = (values) => {
        if (validator.current.allValid()) {
            resetVendorPassword({
                token: token,
                newPassword: values.password,
                confirmPassword: values.confirmPassword,
            });
        } else {
            validator.current.showMessages();
        }
    };

    useEffect(() => {
        if (resetPasswordData?.status === true) {
            navigate("/vendor/login");
        }
    }, [resetPasswordData?.status]);

    return (
        <div className="vendor-auth">
            <div className="va-wrap">
                <Row className="va-card g-0 mx-0">
                    
                    <Col lg="6" className="va-brand">
                        <VendorWaveTopSvg />
                        <div className="va-glow" />

                        <img className="va-logo" src={logoLight} alt="HRA" />

                        <div className="va-laptop">
                            <div className="va-screen">
                                <div className="va-dash">
                                    <div className="va-side">
                                        <i /><i /><i /><i /><i />
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <div className="va-bars">
                                            <b /><b /><b /><b /><b />
                                        </div>
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <VendorDonutSvg />
                                        <div className="va-rows">
                                            <i /><i /><i />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="va-base" />
                        </div>

                        <VendorAuthChips />

                        <VendorWaveBottomSvg />
                    </Col>

                    
                    <Col lg="6" className="va-form-side">
                        <h1 className="va-title">Reset Password</h1>
                        <p className="va-sub">
                            {email ? (
                                <>
                                    Set a new password for{" "}
                                    <span className="va-email">{email}</span>.
                                </>
                            ) : (
                                "Choose a new password for your vendor account."
                            )}
                        </p>

                        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                            {({ values }) => {
                                return (
                                    <Form role={"form"} className="form-login">
                                        <FormGroup className="va-field">
                                            <Label className="va-label" htmlFor="password">
                                                New Password <span className="va-required">*</span>
                                            </Label>
                                            <div className="va-control">
                                                <span className="va-ic">
                                                    <LockSvg />
                                                </span>
                                                <Field
                                                    id="password"
                                                    name="password"
                                                    type={showPassword ? "text" : "password"}
                                                    className="va-input"
                                                    placeholder="Enter your new password"
                                                    autoComplete="new-password"
                                                    maxLength={99}
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="va-eye"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                >
                                                    {showPassword ? <EyeOffOutlineSvg /> : <EyeOutlineSvg />}
                                                </button>
                                            </div>
                                            
                                            {validator.current.message(
                                                __t("validations.auth.password_required"),
                                                values.password,
                                                [
                                                    "required",
                                                    { min: 6 },
                                                    { regex: /^(?=.*\d)(?=.*[!@#$%^&*_=])(?=.*[a-z])(?=.*[A-Z]).{6,}$/ },
                                                ],
                                                {
                                                    messages: {
                                                        required: __t("validations.auth.password_required"),
                                                        min: __t("validations.auth.min_password"),
                                                        regex: __t("validations.auth.password_pattern"),
                                                    },
                                                }
                                            )}
                                        </FormGroup>

                                        <FormGroup className="va-field">
                                            <Label className="va-label" htmlFor="confirmPassword">
                                                Confirm Password <span className="va-required">*</span>
                                            </Label>
                                            <div className="va-control">
                                                <span className="va-ic">
                                                    <LockSvg />
                                                </span>
                                                <Field
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    className="va-input"
                                                    placeholder="Re-enter your new password"
                                                    autoComplete="new-password"
                                                    maxLength={99}
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="va-eye"
                                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                >
                                                    {showConfirmPassword ? <EyeOffOutlineSvg /> : <EyeOutlineSvg />}
                                                </button>
                                            </div>
                                            {validator.current.message(
                                                __t("validations.auth.confirm_password"),
                                                values.confirmPassword,
                                                ["required", { in: [values.password] }],
                                                {
                                                    messages: {
                                                        required: __t("validations.auth.confirm_password"),
                                                        in: __t("validations.auth.confirm_password_match"),
                                                    },
                                                }
                                            )}
                                            <FormText className="va-hint">
                                                Password must contain at least 1 uppercase, 1 lowercase, 1 digit and 1
                                                special symbol with minimum length of 6 characters.
                                            </FormText>
                                        </FormGroup>

                                        <div className="va-row va-row-end">
                                            <Link className="va-forgot" to="/vendor/login">
                                                Back to login
                                            </Link>
                                        </div>

                                        <Button type="submit" className="va-btn">
                                            Reset Password
                                        </Button>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </Col>
                </Row>
            </div>

            <div className="va-footer">
                <a href="https://www.hraga.com/terms-of-service" target="_blank" rel="noreferrer" title="Terms of Service">
                    Terms of Service
                </a>
                •
                <a href="https://www.hraga.com/mbr-app-privacy-policy" target="_blank" rel="noreferrer" title="Privacy Policy">
                    Privacy Policy
                </a>
                •
                <span className="va-copy">© {new Date().getFullYear()} HRA. All rights reserved.</span>
            </div>
        </div>
    );
};

const mapStateToProps = ({ VendorForgot }) => {
    return {
        resetPasswordData: VendorForgot?.resetPassword || null,
        confirmTokenData: VendorForgot?.confirmToken || null,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        confirmResetToken: (token) => dispatch(vendorConfirmResetTokenRequest(token)),
        resetVendorPassword: (data) => dispatch(vendorResetPasswordRequest(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorResetPassword);
